<script lang="ts" setup>
import Button from "@/components/Reusables/Buttons/Button.vue";
import EmptyState from "@/components/Reusables/EmptyState/EmptyState.vue";
import ProjectCard from "../components/ProjectCard.vue";
import ProjectCardSkeleton from "../components/ProjectCardSkeleton.vue";
import ProjectTableItem from "../components/ProjectTableItem.vue";
import ProjectTable from "../components/ProjectTable.vue";
import { EmissionBudgetState } from "@/api/composables/useEmissionBudgets.composable";
import { GetProjectListQuery, Order_By } from "@/graphql";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import User from "@/types/auth/User";
import { useMediaQuery } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, VNodeRef, watch } from "vue";
import DefaultSkeletonCount from "../constants/DefaultSkeletonCount";
import { ProjectListSortOption } from "../constants/SortOptions";
import type { ProjectCompareItem } from "../composables/useProjectSelection";
import { useStartProjectModal } from "../composables/useStartProjectModal.composable";

type SortField = ProjectListSortOption["orderBy"]["field"];

const authState = useAuthStateStore();
const { user } = storeToRefs(authState);
const hasLoadedUser = computed(() => !!user.value);

// The table (list) view has no room below the `md` breakpoint, so a saved
// "list" view falls back to the card grid there instead of rendering blank.
const isDesktop = useMediaQuery("(min-width: 768px)");
const effectiveView = computed(() => (props.view === "list" && !isDesktop.value ? "grid" : props.view));

const scrollRoot = ref<HTMLElement | null>(null);
const visibleIds = ref(new Set<string>());
const props = defineProps<{
  emissionBudgetsByProjectId: Record<string, EmissionBudgetState>;
  loading: boolean;
  hasActiveFilters?: boolean;
  projects: GetProjectListQuery["project"];
  selectedForBulk?: string[];
  skeletonCount?: number;
  sortDirection?: Order_By;
  sortField?: SortField | null;
  tenantUsersById: { [key: string]: User };
  view: "list" | "grid";
}>();

const emit = defineEmits<{
  (e: "refetchEmissionBudget", projectId: string): void;
  (e: "projectsInViewPort", projectIds: string[]): void;
  (e: "sort", field: SortField): void;
  (e: "compareItem", item: ProjectCompareItem): void;
}>();

const scrollToTop = () => {
  const scrollParent = getScrollParent(scrollRoot.value);
  (scrollParent ?? scrollRoot.value)?.scrollTo({ behavior: "smooth", top: 0 });
};

defineExpose({ scrollToTop });

const elById = new Map<string, Element>();
let io: IntersectionObserver | null = null;
const projectsInViewPort = ref<Record<string, boolean>>({});

onMounted(async () => {
  await recreateObserver();
});

onBeforeUnmount(() => {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  io?.disconnect();
  io = null;
  elById.clear();
  visibleIds.value.clear();
});

let debounceTimer: number | null = null;
const emitVisible = () => {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = window.setTimeout(() => {
    debounceTimer = null;
    onProjectsInViewPort(Array.from(visibleIds.value));
  }, 1000);
};

const onProjectsInViewPort = (projectIds: string[]) => {
  projectsInViewPort.value = {
    ...projectsInViewPort.value,
    ...Object.fromEntries(projectIds.map((id) => [id, true])),
  };

  emit("projectsInViewPort", projectIds);
};

const recreateObserver = async () => {
  await nextTick();

  const root = getScrollParent(scrollRoot.value) ?? null;

  io?.disconnect();

  io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const id = (entry.target as HTMLElement).dataset.projectId;
        if (!id) continue;

        setVisible(id, entry.isIntersecting);
      }
    },
    {
      root,
      threshold: 0.25,
      rootMargin: "0px",
    }
  );

  for (const el of elById.values()) io.observe(el);
};

const setProjectEl = (id: string) => (el: Element | null) => {
  const prev = elById.get(id);

  if (prev && io) io.unobserve(prev);

  if (!el) {
    elById.delete(id);
    if (visibleIds.value.delete(id)) emitVisible();
    return;
  }

  elById.set(id, el);
  if (io) io.observe(el);
};

const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let cur: HTMLElement | null = el;
  while (cur) {
    const style = window.getComputedStyle(cur);
    const overflowY = style.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return cur;
    cur = cur.parentElement;
  }
  return null;
};

const setVisible = (id: string, isVisible: boolean) => {
  const before = visibleIds.value.size;
  if (isVisible) visibleIds.value.add(id);
  else visibleIds.value.delete(id);

  if (visibleIds.value.size !== before) emitVisible();
};

watch(
  props.projects,
  async (newProjects) => {
    await nextTick();

    const allowed = new Set(newProjects.map((p) => p.id));

    // remove ids that no longer exist
    let changed = false;
    for (const id of visibleIds.value) {
      if (!allowed.has(id)) {
        visibleIds.value.delete(id);
        changed = true;
      }
    }
    if (changed) emitVisible();

    // ensure observer exists and observe all nodes
    if (!io && scrollRoot.value) {
      await recreateObserver();
    } else if (io) {
      for (const el of elById.values()) io.observe(el);
    }
  },
  { flush: "post" }
);

// Optional: if grid/list view toggles causes DOM rebuild, recreate observer.
// Watch the effective view so crossing the `md` breakpoint (which rebuilds the
// table into cards, and vice versa) also re-registers the observed nodes.
watch(effectiveView, async () => {
  await recreateObserver();
});

// Both empty states offer the create action; the dialog is the only door to it now.
const { openStartProjectModal } = useStartProjectModal();
</script>

<template>
  <div
    v-if="hasLoadedUser"
    ref="scrollRoot">
    <div
      v-if="effectiveView === 'grid'"
      class="mb-8 grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-[16px]">
      <ProjectCardSkeleton
        v-for="i in skeletonCount || DefaultSkeletonCount"
        v-if="loading"
        :key="`list-card-skeleton-${i}`"
        data-cy="project-list-item" />
      <div
        v-for="project in projects"
        v-else
        :key="project.id"
        class="h-full w-full"
        data-cy="project-card">
        <!-- `h-full` all the way down to the card: the grid stretches its items, so without it the
             card sits at its natural height, top-aligned in the row, and any row a card does not
             render leaves its outline short of its neighbours' (RTLCA-5561). -->
        <div
          :ref="setProjectEl(project.id) as unknown as VNodeRef"
          class="h-full"
          :data-project-id="project.id">
          <ProjectCard
            :emission-budget="emissionBudgetsByProjectId[project.id]"
            :has-access="project.role !== 'accessAllProjects'"
            :has-been-seen="!!projectsInViewPort[project.id]"
            :is-selected-for-bulk="selectedForBulk?.includes(project.id)"
            :project="project"
            :project-user-by-id="tenantUsersById"
            :user-id="user?.id"
            @refetch="() => emit('refetchEmissionBudget', project.id)"
            @compare-item="(item: ProjectCompareItem) => emit('compareItem', item)" />
        </div>
      </div>
    </div>
    <div
      v-else
      class="w-full">
      <ProjectTable
        :loading="loading"
        :skeleton-count="skeletonCount"
        :sort-direction="sortDirection"
        :sort-field="sortField"
        @sort="(field: SortField) => emit('sort', field)">
        <RouterLink
          v-for="project in projects"
          v-slot="{ href, navigate }"
          :key="project.id"
          custom
          :to="`/project/${project.id}`">
          <a
            :href="href"
            class="group/row block"
            @click="navigate">
            <div
              :ref="setProjectEl(project.id) as unknown as VNodeRef"
              :data-project-id="project.id">
              <ProjectTableItem
                class="sm:group-last/row:rounded-b-16"
                :emission-budget="emissionBudgetsByProjectId[project.id]"
                :is-selected-for-bulk="selectedForBulk?.includes(project.id)"
                :project="project"
                @compare-item="(item: ProjectCompareItem) => emit('compareItem', item)" />
            </div>
          </a>
        </RouterLink>
      </ProjectTable>
    </div>
    <div
      v-if="loading === false && hasActiveFilters && projects.length === 0"
      class="mx-4 mt-24 mb-4 flex w-full flex-col items-center justify-center">
      <EmptyState
        icon="fa-solid fa-folder-open"
        title="No projects were found!"
        description="Perhaps you should add one?"
        size="lg">
        <template #actions>
          <Button
            label="Add project"
            color="primary"
            @click="openStartProjectModal" />
        </template>
      </EmptyState>
    </div>
    <div v-if="loading === false && !hasActiveFilters && projects.length === 0">
      <div class="mt-24 mb-4 flex w-full flex-col items-center justify-center px-8 text-center">
        <EmptyState
          icon="fa-solid fa-seedling"
          title="Welcome to Real-Time LCA - the platform for sustainable building projects!"
          description="This is the project overview, but currently you don't have any projects. Perhaps you should add one?"
          size="lg">
          <template #actions>
            <Button
              id="empty-state-add-project-button"
              label="Add your first project"
              color="super-cta-primary"
              @click="openStartProjectModal" />
          </template>
        </EmptyState>
      </div>
    </div>
  </div>
</template>
